import { configureStore } from '@reduxjs/toolkit';
import msalAccountSlice from '@/features/msalAccount/msalAccountSlice';
import dealsTableSlice from '@/features/table/dealsTableSlice';
import clientsTableSlice from '@/features/table/clientsTableSlice';
import claimsTableSlice from '@/features/table/claimsTableSlice';
import attachmentsTableSlice from '@/features/table/attachmentsTableSlice';
import instantSearchSlice from '@/features/table/instantSearchSlice';
import tasksTableSlice from '@/features/table/tasksTableSlice';
import clientSelectSlice from '@/features/select/clientSelectSlice';
import dealSelectSlice from '@/features/select/dealSelectSlice';
import userPrefsSlice from '@/features/userPrefs/userPrefsSlice';
import notificationsSlice from '@/features/notificationsSlice/notificationsSlice';
import toastSlice from '@/features/toast/toastSlice';
import dialogSlice from '@/features/dialog/dialogSlice';
import engagementsSlice from '@/features/engagements/engagementsSlice';
import sideNavSlice from '@/features/sideNav/sideNavSlice';
import templatesSlice from '@/features/templates/templatesSlice';
import { setupListeners } from '@reduxjs/toolkit/query';
import apis from './apis';
import { accountChangeMiddleware } from './middleware';
import insightDebtorsPrefsSlice from '@/features/table/debtorsTableSlice';
import renewalReportPrefsSlice from '@/features/table/renewalReportTableSlice';
import relationshipContactsTableSlice from '@/features/table/relationshipContactsTableSlice';
import relationshipCompaniesTableSlice from '@/features/table/relationshipCompaniesTableSlice';
import clientContactsPrefsSlice from '@/features/table/clientContactsPrefsSlice';
import auditTablePrefsReducer from '@/features/table/auditTableSlice';
import firebaseSlice from '@/features/authorisationSlice/authorisationSlice';
/**
 * In this configuration, we set up a Redux store using configureStore from Redux Toolkit.
 * This store includes various slices and API middleware.
 *
 * The msalAccountSlice manages the state related to Microsoft Authentication Library (MSAL) account,
 * while the API slices (userApi, clientsApi, etc.) manage the state and side-effects for
 * different data entities like users, clients, deals, etc.
 *
 * Each API slice's reducer is dynamically added to the store using its reducerPath. The middleware
 * from each API slice is also included, allowing for efficient handling of async operations and
 * caching, which are common when working with APIs in a Redux application.
 *
 * This setup ensures a modular and scalable structure for your application's state management.
 */
const storeReducers = apis.reduce((acc, api) => ({ ...acc, [api.reducerPath]: api.reducer }), {});

const store = configureStore({
	reducer: {
		// Adding slices to the store
		msalAccount: msalAccountSlice,
		dealTable: dealsTableSlice,
		clientTable: clientsTableSlice,
		claimTable: claimsTableSlice,
		attachmentsTable: attachmentsTableSlice,
		taskTable: tasksTableSlice,
		clientSelect: clientSelectSlice,
		dealSelect: dealSelectSlice,
		userPrefs: userPrefsSlice,
		notifications: notificationsSlice,
		toast: toastSlice,
		dialog: dialogSlice,
		engagementsSlice: engagementsSlice,
		sideNavSlice: sideNavSlice,
		instantSearch: instantSearchSlice,
		templatesSlice: templatesSlice,
		debtorsFilterPrefs: insightDebtorsPrefsSlice,
		renewalReportPrefs: renewalReportPrefsSlice,
		relationshipContactsTable: relationshipContactsTableSlice,
		relationshipCompaniesTable: relationshipCompaniesTableSlice,
		clientContactsPrefs: clientContactsPrefsSlice,
		auditTablePrefs: auditTablePrefsReducer,
		firebase: firebaseSlice,
		// API slices are added dynamically to the store using dynamic keys from
		// each API's reducerPath (kept in /store/apis.js apis array)
		...storeReducers,
	},
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware().concat(
			// Adding API middlewares to intercept and handle API-related actions
			...apis.map((api) => api.middleware),
			accountChangeMiddleware,
		),
});

setupListeners(store.dispatch);

export default store;
